import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Calculator,
  Trash2, MapPin, ExternalLink, Package, X,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  HIGH_BUILD_EPOXY,
  FLAKE_PRODUCTS,
  POLYASPARTIC_SEAL,
  HIGH_BUILD_SYSTEMS,
  FLAKE_COVERAGE,
  HIGH_BUILD_COLOUR_CHART,
} from '@/components/proepoxy/productData';
import { calculateOptimalPacks } from '@/components/proepoxy/utils';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import PrioritySelector from '@/components/PrioritySelector';

export default function HighBuildGuidedStep3({ state, priority, setPriority, currentStep, goToStep, highBuildOrderMode, setHighBuildOrderMode, navigate, onExit }) {
  const {
    highBuildAreas, manualOrder,
    showInstallationGuide, setShowInstallationGuide,
    showColourChart, setShowColourChart,
    highBuildSteps,
    addHighBuildArea, removeHighBuildArea, updateHighBuildArea,
    initializeHighBuildProducts,
    calculateHighBuildTotal, getHighBuildItemCount,
    addPrimerItem, updatePrimerItem, removePrimerItem,
    addHighBuildItem, updateHighBuildItem, removeHighBuildItem,
    addFlakeItem, updateFlakeItem, removeFlakeItem,
    updateManualOrder, setManualOrder,
    calculateManualOrderTotal, getManualOrderItemCount,
    updateManualFloorPrepQty,
  } = state;
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-[#27292E]">System Selection</h2>
                    <Button
                      onClick={() => setShowInstallationGuide(true)}
                      variant="outline"
                      className="border-2 border-[#7A0B7A] text-[#7A0B7A]"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Installation Guide
                    </Button>
                  </div>
                  <p className="text-gray-600 mb-6">Select the system type for each area.</p>

                  {/* Installation Guide Modal */}
                  {showInstallationGuide && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-2xl max-w-lg w-full p-6 relative">
                        <button onClick={() => setShowInstallationGuide(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                          <span className="text-2xl">×</span>
                        </button>
                        <h3 className="text-xl font-bold text-[#27292E] mb-4">Installation Guide</h3>
                        
                        <div className="space-y-4">
                          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                            <h4 className="font-bold text-green-800 mb-1">Best High Build Epoxy Installation</h4>
                            <p className="text-sm text-green-700">1 x Epoxy Prime Coat + 2 x High Build Epoxy Coats</p>
                          </div>
                          
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                            <h4 className="font-bold text-blue-800 mb-1">Standard High Build Epoxy Installation</h4>
                            <p className="text-sm text-blue-700">1 x Epoxy Prime Coat + 1 x High Build Epoxy Coat</p>
                          </div>
                          
                          <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
                            <h4 className="font-bold text-purple-800 mb-1">Flake Floor Installation</h4>
                            <p className="text-sm text-purple-700">1 x Epoxy Prime Coat + 1 x High Build Epoxy (Scatter Flakes into this coat) + 1 x Polyaspartic Seal Coat</p>
                          </div>
                        </div>

                        <Button onClick={() => setShowInstallationGuide(false)} className="w-full mt-6 bg-[#7A0B7A] hover:bg-[#5a085a] text-white">
                          Got it
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Per-Area System Selection */}
                  <div className="space-y-6">
                    {highBuildAreas.map((area, index) => (
                      <div key={area.id} className="border-2 border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                          <div>
                            <h3 className="font-bold text-[#27292E]">{area.name}</h3>
                            <p className="text-sm text-gray-500">{area.m2}m²</p>
                          </div>
                          <span className="px-3 py-1 bg-[#7A0B7A]/10 text-[#7A0B7A] rounded-full text-sm font-medium">{area.m2}m²</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {Object.entries(HIGH_BUILD_SYSTEMS).map(([key, system]) => (
                            <button
                              key={key}
                              onClick={() => updateHighBuildArea(area.id, 'systemType', key)}
                              className={`p-4 rounded-xl text-left transition-all border-2 ${
                                area.systemType === key ? 'border-[#7A0B7A] bg-[#7A0B7A]/5' : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <h4 className="font-medium text-[#27292E]">{system.name}</h4>
                              <p className="text-xs text-gray-500 mt-1">{system.description}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-sm text-gray-500 mt-4 text-center italic">Products can be adjusted on the next step</p>
                </div>

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button onClick={() => goToStep(2)} variant="outline" className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5">
                    <ArrowLeft className="w-5 h-5 mr-2" />Back
                  </Button>
                  <Button 
                    onClick={() => {
                      // Initialize products for all areas
                      highBuildAreas.forEach(area => initializeHighBuildProducts(area.id));
                      goToStep(4);
                    }}
                    disabled={highBuildAreas.some(a => !a.systemType)}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl disabled:opacity-50"
                  >
                    Continue<ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
  );
}
